/**
 * Sidebar variant for the "Departments" contour.
 *
 * Body lists health filter chips (healthy / lagging / failed), recent
 * departments visited (last 5, from localStorage), and quick links to
 * "Open escalations" + "My queue".
 */
import type { DepartmentHealthState } from '@chaos/types/department';
import { useQuery } from '@tanstack/react-query';
import { Link, useMatchRoute, useNavigate } from '@tanstack/react-router';
import {
  AlertTriangle,
  ChevronsLeft,
  ChevronsUpDown,
  ListChecks,
  LogOut,
  Moon,
  Settings,
  Sun,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { authApi } from '../../lib/api';
import { departmentsApi, departmentsKeys } from '../../lib/api/departments';
import { useResizablePanel } from '../../lib/use-resizable-panel';
import { cn } from '../../lib/utils';
import { useAuthStore } from '../../stores/auth-store';
import { useThemeStore } from '../../stores/theme-store';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';

interface DepartmentsSidebarProps {
  collapsed: boolean;
  onToggle: () => void;
}

const RECENT_KEY = 'chaos-departments-recent';

function readRecent(): string[] {
  try {
    if (typeof localStorage === 'undefined') return [];
    const raw = localStorage.getItem(RECENT_KEY);
    return raw ? (JSON.parse(raw) as string[]) : [];
  } catch {
    return [];
  }
}

export function trackRecentDepartment(deptKey: string): void {
  try {
    if (typeof localStorage === 'undefined') return;
    const prev = readRecent().filter((k) => k !== deptKey);
    const next = [deptKey, ...prev].slice(0, 5);
    localStorage.setItem(RECENT_KEY, JSON.stringify(next));
  } catch {
    /* ignore */
  }
}

type HealthFilter = 'all' | DepartmentHealthState;

export function DepartmentsSidebar({ collapsed, onToggle }: DepartmentsSidebarProps) {
  const { t } = useTranslation();
  const matchRoute = useMatchRoute();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const workspaces = useAuthStore((s) => s.workspaces);
  const activeWorkspaceId = useAuthStore((s) => s.activeWorkspaceId);
  const setUnauthenticated = useAuthStore((s) => s.setUnauthenticated);
  const theme = useThemeStore((s) => s.theme);
  const setTheme = useThemeStore((s) => s.setTheme);

  const activeWorkspace = workspaces.find((w) => w.workspace.id === activeWorkspaceId)?.workspace;

  const { width, isDragging, onHandleMouseDown } = useResizablePanel({
    storageKey: 'chaos-sidebar-width',
    defaultWidth: 260,
    minWidth: 220,
    maxWidth: 380,
    side: 'left',
  });

  const listQ = useQuery({
    queryKey: departmentsKeys.list(),
    queryFn: () => departmentsApi.list(),
  });

  const [filter, setFilter] = useState<HealthFilter>('all');
  const [recent, setRecent] = useState<string[]>([]);

  useEffect(() => {
    setRecent(readRecent());
    const handler = (): void => setRecent(readRecent());
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, []);

  const departments = listQ.data?.departments ?? [];
  const recentDepartments = useMemo(
    () =>
      recent
        .map((key) => departments.find((d) => d.key === key))
        .filter((d): d is NonNullable<typeof d> => Boolean(d)),
    [recent, departments],
  );

  const counts = useMemo(() => {
    let healthy = 0;
    let lagging = 0;
    let failed = 0;
    for (const d of departments) {
      if (d.health === 'green') healthy += 1;
      else if (d.health === 'yellow') lagging += 1;
      else if (d.health === 'red') failed += 1;
    }
    return { healthy, lagging, failed };
  }, [departments]);

  const handleSignOut = async (): Promise<void> => {
    try {
      await authApi.logout();
    } catch {
      /* ignore */
    }
    setUnauthenticated();
    window.location.href = '/login';
  };

  if (collapsed) return null;

  return (
    <aside
      aria-label="Departments navigation"
      style={{ width }}
      className={cn(
        'relative flex h-screen shrink-0 flex-col border-r border-border bg-muted/30',
        isDragging && 'select-none',
      )}
    >
      <div className="flex items-center gap-1">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="group flex flex-1 items-center justify-between px-3 py-2.5 text-left hover:bg-foreground/5"
              aria-label="Account menu"
            >
              <div className="flex min-w-0 flex-col leading-tight">
                <span className="truncate text-[13px] font-semibold tracking-tight text-foreground">
                  {activeWorkspace?.name ?? user?.displayName ?? user?.email ?? t('app.name')}
                </span>
                <span className="truncate text-[11px] text-muted-foreground">
                  {user?.email ?? ''}
                </span>
              </div>
              <ChevronsUpDown
                className="h-3.5 w-3.5 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100"
                aria-hidden
              />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" side="bottom" className="w-56">
            <DropdownMenuLabel>
              <div className="flex flex-col gap-0.5">
                <span className="text-sm font-medium">{user?.displayName ?? user?.email}</span>
                <span className="text-xs text-muted-foreground">{user?.email}</span>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}>
              {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
              <span>{theme === 'dark' ? t('nav.light_mode') : t('nav.dark_mode')}</span>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link to="/settings/profile">
                <Settings className="h-4 w-4" />
                <span>{t('settings.profile.title')}</span>
              </Link>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleSignOut}>
              <LogOut className="h-4 w-4" />
              <span>{t('dashboard.sign_out')}</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        <button
          type="button"
          onClick={onToggle}
          aria-label="Collapse sidebar"
          className="mr-1.5 flex h-7 w-7 items-center justify-center rounded text-muted-foreground transition-colors hover:bg-foreground/5 hover:text-foreground"
        >
          <ChevronsLeft className="h-4 w-4" aria-hidden />
        </button>
      </div>

      <nav className="flex-1 overflow-y-auto px-2 pb-2 pt-3">
        <div className="px-2 text-[11px] font-medium uppercase tracking-wider text-muted-foreground/80">
          Departments
        </div>

        <ul className="mt-1 flex flex-col">
          <li>
            <Link
              to="/departments"
              className={cn(
                'flex items-center rounded px-2 py-1 text-[13px] transition-colors',
                matchRoute({ to: '/departments' })
                  ? 'bg-foreground/5 text-foreground'
                  : 'text-muted-foreground hover:bg-foreground/5 hover:text-foreground',
              )}
            >
              Overview
            </Link>
          </li>
        </ul>

        <div className="mt-4 px-2 text-[11px] font-medium uppercase tracking-wider text-muted-foreground/80">
          Filter by health
        </div>
        <div className="mt-1 flex flex-wrap gap-1 px-2">
          <FilterChip
            label="All"
            count={departments.length}
            active={filter === 'all'}
            onClick={() => {
              setFilter('all');
              void navigate({ to: '/departments' });
            }}
          />
          <FilterChip
            label="Healthy"
            tone="success"
            count={counts.healthy}
            active={filter === 'green'}
            onClick={() => {
              setFilter('green');
              void navigate({ to: '/departments', search: { health: 'green' } });
            }}
          />
          <FilterChip
            label="Lagging"
            tone="warning"
            count={counts.lagging}
            active={filter === 'yellow'}
            onClick={() => {
              setFilter('yellow');
              void navigate({ to: '/departments', search: { health: 'yellow' } });
            }}
          />
          <FilterChip
            label="Failed"
            tone="error"
            count={counts.failed}
            active={filter === 'red'}
            onClick={() => {
              setFilter('red');
              void navigate({ to: '/departments', search: { health: 'red' } });
            }}
          />
        </div>

        <div className="mt-4 px-2 text-[11px] font-medium uppercase tracking-wider text-muted-foreground/80">
          Recent
        </div>
        <ul className="mt-1 flex flex-col">
          {recentDepartments.length === 0 ? (
            <li className="px-2 py-1 text-xs text-muted-foreground/70">No recent.</li>
          ) : (
            recentDepartments.map((dept) => {
              const isActive = !!matchRoute({
                to: '/departments/$deptKey',
                params: { deptKey: dept.key },
                fuzzy: true,
              });
              return (
                <li key={dept.key}>
                  <Link
                    to="/departments/$deptKey"
                    params={{ deptKey: dept.key }}
                    search={{ tab: 'overview' }}
                    className={cn(
                      'flex items-center gap-1.5 rounded px-2 py-1 text-[13px] transition-colors',
                      isActive
                        ? 'bg-foreground/5 text-foreground'
                        : 'text-muted-foreground hover:bg-foreground/5 hover:text-foreground',
                    )}
                    title={dept.mission ?? dept.displayName}
                  >
                    <span
                      aria-hidden
                      className={cn(
                        'h-1.5 w-1.5 shrink-0 rounded-full',
                        dept.health === 'green' && 'bg-success',
                        dept.health === 'yellow' && 'bg-warning',
                        dept.health === 'red' && 'bg-destructive',
                        dept.health === 'unknown' && 'bg-muted-foreground/40',
                      )}
                    />
                    <span className="truncate">{dept.displayName}</span>
                  </Link>
                </li>
              );
            })
          )}
        </ul>

        <div className="mt-4 px-2 text-[11px] font-medium uppercase tracking-wider text-muted-foreground/80">
          Quick links
        </div>
        <ul className="mt-1 flex flex-col">
          <li>
            <Link
              to="/departments"
              search={{ tab: 'escalations' }}
              className="flex items-center gap-2 rounded px-2 py-1 text-[13px] text-muted-foreground transition-colors hover:bg-foreground/5 hover:text-foreground"
            >
              <AlertTriangle className="h-3.5 w-3.5" aria-hidden />
              <span>Open escalations</span>
            </Link>
          </li>
          <li>
            <Link
              to="/departments"
              search={{ tab: 'my_queue' }}
              className="flex items-center gap-2 rounded px-2 py-1 text-[13px] text-muted-foreground transition-colors hover:bg-foreground/5 hover:text-foreground"
            >
              <ListChecks className="h-3.5 w-3.5" aria-hidden />
              <span>My queue</span>
            </Link>
          </li>
        </ul>
      </nav>

      <button
        type="button"
        aria-label="Resize sidebar"
        tabIndex={-1}
        onMouseDown={onHandleMouseDown}
        className={cn(
          'absolute right-0 top-0 z-10 h-full w-1 cursor-col-resize border-0 bg-transparent p-0',
          'transition-colors',
          isDragging ? 'bg-foreground/20' : 'hover:bg-foreground/10',
        )}
      />
    </aside>
  );
}

interface FilterChipProps {
  label: string;
  count: number;
  active: boolean;
  onClick: () => void;
  tone?: 'default' | 'success' | 'warning' | 'error';
}

function FilterChip({ label, count, active, onClick, tone = 'default' }: FilterChipProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'inline-flex items-center gap-1.5 rounded px-2 py-0.5 text-[11px] transition-colors',
        active
          ? 'bg-foreground/8 text-foreground'
          : 'text-muted-foreground hover:bg-foreground/5 hover:text-foreground',
      )}
    >
      <span
        aria-hidden
        className={cn(
          'h-1.5 w-1.5 rounded-full',
          tone === 'success' && 'bg-success',
          tone === 'warning' && 'bg-warning',
          tone === 'error' && 'bg-destructive',
          tone === 'default' && 'bg-muted-foreground/40',
        )}
      />
      <span>{label}</span>
      <span className="text-muted-foreground/70 tabular-nums">{count}</span>
    </button>
  );
}
